import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React__default from 'react';
import { keyframes, styled, css } from 'styled-components';
import { Button } from './Button.js';
import { cssRadixPopoverContentOverride } from './Drawer/cssRadixPopoverOverride.js';
import { Drawer } from './Drawer/Drawer.js';
import { DropdownItemIconContainer, cssDropdownItemMobile } from './DropdownMenu/Item.js';
import { FieldDescription, FieldLabel, FieldHeader, FieldGroup } from './Field.js';
import { preventForwardPropsConfig } from '../utils/preventForwardProps.js';
import { motion } from 'framer-motion';
import { cssVar } from '../theme/variables.js';
import { ScrollArea } from './ScrollArea.js';
import { IconChevronDownFill } from '@fabric-app/icons/old';
import { componentBaseConfig } from '../styled-components/componentBaseConfig.js';
import { mediaMobile, mediaNotMobile } from '../constants/styled-utils.js';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useResponsive } from '../hooks/useResponsive.js';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { IconChevronDown, IconMobileSheet24MiscTick, IconDropdown14Tick } from '@fabric-app/icons/svg';
import { ResponsiveIcon } from './ResponsiveIcon.js';
import { DrawerDragHandle, DrawerTitle } from './Modal/Drawer.js';

const animateIn = keyframes([
  `from{opacity:0;transform:translate(var(--select-translate-x),var(--select-translate-y));}to{opacity:1;transform:translateY(0);}`
]);
const animateOut = keyframes([
  `from{opacity:1;transform:translateY(0);}to{opacity:0;transform:translate(var(--select-translate-x),var(--select-translate-y));}`
]);
const SelectChevronDown = styled(IconChevronDown).withConfig({
  displayName: "Select__SelectChevronDown",
  componentId: "sc-c6dfe371-0"
})([
  `color:`,
  `;transition:transform 200ms;[data-state='open'] &{transform:rotate(180deg);}[data-state='closed'] &{transform:rotate(0deg);}`
], cssVar["color-text-tertiary"]);
const SelectChevronDownFill = styled(IconChevronDownFill).withConfig({
  displayName: "Select__SelectChevronDownFill",
  componentId: "sc-c6dfe371-1"
})([
  `color:`,
  `;transition:transform 200ms;transform:rotate(0deg);[data-state='open'] &{transform:rotate(180deg);}[data-state='closed'] &{transform:rotate(0deg);}`
], cssVar["color-text-tertiary"]);
const SelectTriggerIcon = styled(SelectPrimitive.Icon).attrs((props) => {
  return {
    asChild: true,
    children: /* @__PURE__ */ jsx(SelectChevronDown, {}),
    ...props
  };
}).withConfig({
  displayName: "Select__SelectTriggerIcon",
  componentId: "sc-c6dfe371-2"
})([
  ``
]);
const cssSelectTriggerButtonVariants = ({ selectVariant: variant }) => {
  switch (variant) {
    case "inline":
      return css([
        `font-weight:500;border-radius:16px;&[data-state='open']{border-radius:16px 16px 0 0;border-bottom-color:transparent;}> svg{font-size:0.7em;}`
      ]);
    case "discreet":
      return css([
        `border-radius:0.625rem;font-size:0.75rem;font-weight:600;padding:0.375rem 0.625rem;`
      ]);
    case "default":
    default:
      return css([
        `font-weight:500;color:`,
        `;`
      ], cssVar["color-text-tertiary"]);
  }
};
const SelectTriggerButton = styled(Button).withConfig(componentBaseConfig).attrs((props) => {
  return {
    variant: "bg-primary",
    size: props.selectVariant === "inline" ? "sm" : "default",
    as: SelectPrimitive.Trigger,
    ...props
  };
}).withConfig({
  displayName: "Select__SelectTriggerButton",
  componentId: "sc-c6dfe371-3"
})([
  `font-size:14px;font-weight:400;padding:0 12px;display:flex;gap:10px;`,
  ` > span:first-child{flex:1;display:inline-flex;align-items:center;gap:0.5rem;}`,
  ` `,
  ` &:disabled{color:`,
  `;`,
  `{opacity:0.5;}}`,
  ``
], (p) => p.size === "xs" && `padding: .5rem; gap: 4px;`, (p) => p.selectVariant === "discreet" && css([
  `> span:first-child{display:inline;overflow:hidden;text-overflow:ellipsis;min-width:0;}`
]), (props) => props.fixedWidth && css([
  `width:`,
  `px;`
], props.fixedWidth), cssVar["color-text-quaternary"], SelectTriggerIcon, cssSelectTriggerButtonVariants);
SelectTriggerButton.displayName = SelectPrimitive.Trigger.displayName;
const SelectTrigger = styled(SelectTriggerButton).attrs(({ placeholder, ...props }) => {
  return {
    ...props,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsx(SelectPrimitive.Value, {
          placeholder
        }),
        props.selectVariant !== "discreet" && /* @__PURE__ */ jsx(SelectTriggerIcon, {
          children: props.selectVariant === "inline" ? /* @__PURE__ */ jsx(SelectChevronDownFill, {}) : /* @__PURE__ */ jsx(SelectChevronDown, {})
        })
      ]
    })
  };
}).withConfig(preventForwardPropsConfig([
  "hideOptionIcon"
])).withConfig({
  displayName: "Select__SelectTrigger",
  componentId: "sc-c6dfe371-4"
})([
  ``,
  ` `,
  `{width:.875rem;[data-fabric-icon-v2="mobile-sheet-24"]{display:none;}[data-fabric-icon-v2="dropdown-14"]{width:.875rem;height:.875rem;}}`
], (p) => p.hideOptionIcon && css([
  ``,
  `{display:none;}`
], DropdownItemIconContainer), DropdownItemIconContainer);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const cssSelectContentVariants = ({ selectVariant: variant }) => {
  switch (variant) {
    case "inline":
      return css([
        `border-top-color:transparent;border-radius:0 0 16px 16px;padding-top:0;min-width:var(--radix-select-trigger-width);width:var(--radix-select-trigger-width);max-width:var(--radix-select-trigger-width);`
      ]);
    case "default":
    default:
      return css([
        ``
      ]);
  }
};
const SelectContentMobile = styled(SelectPrimitive.Content).attrs(({ topContent, selectVariant, open, onOpenChange, ...props }) => {
  React__default.useEffect(() => {
    const onResize = (event) => {
      event.stopImmediatePropagation();
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return {
    position: "popper",
    side: "bottom",
    sideOffset: selectVariant === "inline" ? 0 : 6,
    ...props,
    "data-select-variant": selectVariant,
    children: /* @__PURE__ */ jsxs(Drawer, {
      onOpenChange,
      open,
      children: [
        topContent,
        /* @__PURE__ */ jsxs(ScrollArea.Root, {
          type: "auto",
          children: [
            /* @__PURE__ */ jsx(SelectViewport, {
              children: props.children
            }),
            /* @__PURE__ */ jsx(ScrollArea.Bar, {
              orientation: "vertical",
              children: /* @__PURE__ */ jsx(ScrollArea.Thumb, {})
            })
          ]
        })
      ]
    })
  };
}).withConfig(preventForwardPropsConfig([
  "onOpenChange",
  "open",
  "selectVariant"
])).withConfig({
  displayName: "Select__SelectContentMobile",
  componentId: "sc-c6dfe371-5"
})([
  ``,
  `;`
], cssRadixPopoverContentOverride);
const SelectContent = styled(SelectPrimitive.Content).attrs(({ topContent, selectVariant, ...props }) => {
  return {
    position: "popper",
    side: "bottom",
    sideOffset: selectVariant === "inline" ? 0 : 6,
    ...props,
    "data-select-variant": selectVariant,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        topContent,
        /* @__PURE__ */ jsxs(ScrollArea.Root, {
          type: "auto",
          children: [
            /* @__PURE__ */ jsx(SelectViewport, {
              children: props.children
            }),
            /* @__PURE__ */ jsx(ScrollArea.Bar, {
              orientation: "vertical",
              children: /* @__PURE__ */ jsx(ScrollArea.Thumb, {})
            })
          ]
        })
      ]
    })
  };
}).withConfig(preventForwardPropsConfig([
  "topContent",
  "selectVariant",
  "onOpenChange",
  "open"
])).withConfig({
  displayName: "Select__SelectContent",
  componentId: "sc-c6dfe371-6"
})([
  `background-color:`,
  `;border:1px solid `,
  `;border-radius:6px;overflow:hidden;z-index:126;max-height:var(--radix-select-content-available-height,200px);box-shadow:`,
  ` hsla(`,
  `,0.09);min-width:204px;--select-translate-x:0;--select-translate-y:5px;&[data-side='top']{--select-translate-y:5px;}&[data-side='bottom']{--select-translate-y:-5px;}&[data-side='left']{--select-translate-x:5px;}&[data-side='right']{--select-translate-x:-5px;}&[data-state='open']{animation:`,
  ` 200ms cubic-bezier(0.22,1,0.36,1);}&[data-state='closed']{animation:`,
  ` 200ms cubic-bezier(0.22,1,0.36,1);}`,
  `{width:100%;height:100%;max-height:var(--radix-select-content-available-height,199px);}`,
  ``
], cssVar["color-bg-primary"], cssVar["color-border-primary"], cssVar["shadow-spread-xs"], cssVar["color-text-primary-hsl"], animateIn, animateOut, ScrollArea.Root, cssSelectContentVariants);
const SelectViewport = styled(SelectPrimitive.Viewport).attrs((props) => {
  return {
    ...props,
    asChild: true,
    children: /* @__PURE__ */ jsx(ScrollArea.Viewport, {
      children: props.children
    }),
    style: {
      // https://github.com/radix-ui/primitives/issues/2059
      // This is a workaround to prevent spamming the console with warnings
      overflowY: void 0
    }
  };
}).withConfig({
  displayName: "Select__SelectViewport",
  componentId: "sc-c6dfe371-7"
})([
  `padding:5px;width:100%;height:100%;max-height:var(--radix-select-content-available-height,200px);`,
  `{padding:0 1rem;}`
], mediaMobile);
const SelectItemIndicator = styled(SelectPrimitive.ItemIndicator).attrs((props) => {
  return {
    ...props,
    children: /* @__PURE__ */ jsx(ResponsiveIcon, {
      default: IconDropdown14Tick,
      mobile: IconMobileSheet24MiscTick
    })
  };
}).withConfig({
  displayName: "Select__SelectItemIndicator",
  componentId: "sc-c6dfe371-8"
})([
  `position:absolute;left:0.5rem;display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;[data-select-variant='inline'] &{display:none;}`,
  `{left:auto;right:1rem;display:inline-flex !important;}`
], mediaMobile);
const SelectItemOption = styled(SelectPrimitive.Item).attrs((props) => {
  return {
    ...props,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsx(SelectItemIndicator, {}),
        /* @__PURE__ */ jsx(SelectPrimitive.ItemText, {
          children: props.children
        })
      ]
    })
  };
}).withConfig({
  displayName: "Select__SelectItemOption",
  componentId: "sc-c6dfe371-9"
})([
  `position:relative;padding:6px 8px 6px 32px;user-select:none;display:flex;align-items:center;gap:6px;border-radius:6px;cursor:pointer;transition:background-color 200ms;font-size:14px;font-weight:500;color:`,
  `;> span:last-child{display:inline-flex;align-items:center;flex:1;gap:0.5rem;`,
  `{gap:1rem;}}`,
  ` `,
  `{&[data-highlighted]{background-color:`,
  `;}}[data-select-variant='inline'] &{padding:6px 8px;&[data-state='checked']{display:none;}}&:focus{outline:none;`,
  `{background-color:`,
  `;}}&[data-disabled]{color:`,
  `;cursor:not-allowed;}&,[data-select-variant='inline'] &{`,
  `;}`,
  `{`,
  `{svg[data-fabric-icon-v2='mobile-sheet-24']{display:none;}}`,
  `{svg[data-fabric-icon-v2='dropdown-14']{display:none;}}}`
], cssVar["color-text-dropdown-item"], mediaMobile, cssDropdownItemMobile, mediaNotMobile, cssVar["color-bg-dropdown-item-hover"], mediaNotMobile, cssVar["color-bg-dropdown-item-hover"], cssVar["color-text-tertiary"], cssDropdownItemMobile, DropdownItemIconContainer, mediaNotMobile, mediaMobile);
const SelectItemGroup = SelectPrimitive.Group;
const SelectItemLabel = styled(SelectPrimitive.Label).withConfig({
  displayName: "Select__SelectItemLabel",
  componentId: "sc-c6dfe371-10"
})([
  `padding:8px 12px;font-size:14px;font-weight:500;color:`,
  `;`
], cssVar["color-text-secondary"]);
const SelectSeparator = styled(SelectPrimitive.Separator).withConfig({
  displayName: "Select__SelectSeparator",
  componentId: "sc-c6dfe371-11"
})([
  `background-color:`,
  `;height:1px;margin:8px 0;`
], cssVar["color-border-primary"]);
const SelectOverlay = styled.div.withConfig({
  displayName: "Select__SelectOverlay",
  componentId: "sc-c6dfe371-12"
})([
  `display:block;position:fixed;inset:0;z-index:125;`
]);
const Select = /* @__PURE__ */ React__default.forwardRef(({ children, value, onChange, disabled, label, description, selectVariant = "default", id, placeholder = "Select an option", triggerProps, fieldGroupProps, topContent, side = "bottom", align, title }, ref) => {
  const openState = useBoolState();
  const { isMobileView } = useResponsive();
  const debouncedValue = useDebounce(openState.value, 100);
  const Content = isMobileView ? SelectContentMobile : SelectContent;
  return /* @__PURE__ */ jsxs(FieldGroup, {
    ...fieldGroupProps,
    ref,
    children: [
      (label || description) && /* @__PURE__ */ jsxs(FieldHeader, {
        children: [
          label && /* @__PURE__ */ jsx(FieldLabel, {
            children: label
          }),
          description && /* @__PURE__ */ jsx(FieldDescription, {
            children: description
          })
        ]
      }),
      /* @__PURE__ */ jsx(DialogPrimitive.Root, {
        open: openState.value,
        onOpenChange: openState.set,
        children: /* @__PURE__ */ jsxs(SelectPrimitive.Root, {
          value,
          onValueChange: onChange,
          disabled,
          open: openState.value,
          onOpenChange: (isOpen) => {
            openState.set(isOpen);
          },
          children: [
            /* @__PURE__ */ jsx(SelectTrigger, {
              selectVariant,
              id,
              placeholder,
              ...triggerProps,
              /**
              * Github issue:
              * https://github.com/radix-ui/primitives/issues/1641
              *
              * Scrolling on mobile triggers the Radix select to open wrongfully which is against native behavior.
              * This makes it so that doesn't happen.
              */
              onPointerDown: (e) => {
                if (e.pointerType === "touch") e.preventDefault();
                triggerProps?.onPointerDown?.(e);
              },
              onPointerUp: (e) => {
                if (e.pointerType === "touch" && !disabled) {
                  openState.handleToggle();
                }
              }
            }),
            /* @__PURE__ */ jsxs(DialogPrimitive.Portal, {
              container: getDefaultRadixPortalContainer(),
              forceMount: true,
              children: [
                (debouncedValue || openState.value) && /* @__PURE__ */ jsx(SelectOverlay, {}, "overlay"),
                /* @__PURE__ */ jsx("div", {
                  children: /* @__PURE__ */ jsx(SelectPrimitive.Portal, {
                    container: getDefaultRadixPortalContainer(),
                    children: /* @__PURE__ */ jsxs(Content, {
                      selectVariant,
                      onCloseAutoFocus: (e) => {
                        e.preventDefault();
                      },
                      topContent,
                      onOpenChange: openState.set,
                      open: openState.value,
                      side,
                      align,
                      children: [
                        /* @__PURE__ */ jsx(DrawerDragHandle, {}),
                        title && /* @__PURE__ */ jsx(TitleContainer, {
                          children: /* @__PURE__ */ jsx(DrawerTitle, {
                            children: title
                          })
                        }),
                        children
                      ]
                    })
                  })
                })
              ]
            })
          ]
        })
      })
    ]
  });
});
Select.displayName = "Select";
const Select$1 = Object.assign(Select, {
  Root: SelectPrimitive.Root,
  Portal: SelectPrimitive.Portal,
  Trigger: SelectTrigger,
  TriggerButton: SelectTriggerButton,
  TriggerIcon: SelectTriggerIcon,
  TriggerValue: SelectPrimitive.Value,
  Content: SelectContent,
  Separator: SelectSeparator,
  ItemOption: SelectItemOption,
  ItemOptionIconContainer: DropdownItemIconContainer,
  ItemGroup: SelectItemGroup,
  ItemLabel: SelectItemLabel,
  FieldGroup,
  FieldLabel: FieldHeader,
  FieldLabelTitle: FieldLabel,
  FieldLabelDescription: FieldDescription
});
const MotionSelect = styled(motion(Select)).attrs({
  initial: {
    opacity: 0,
    y: 10
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: 10
  },
  transition: {
    duration: 0.2
  }
}).withConfig({
  displayName: "Select__MotionSelect",
  componentId: "sc-c6dfe371-13"
})([
  ``
]);
const TitleContainer = styled.div.withConfig({
  displayName: "Select__TitleContainer",
  componentId: "sc-c6dfe371-14"
})([
  `margin-bottom:20px;`,
  `{display:none;}`
], mediaNotMobile);

export { MotionSelect, SelectChevronDown, SelectTriggerButton, Select$1 as default };
//# sourceMappingURL=Select.js.map
